import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import PlayerTable from '../components/PlayerTable';
import InsightCard from '../components/InsightCard';
import StatCard from '../components/StatCard';
import { generateLeagueInsight } from '../utils/insights';

export default function LeagueDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: leagueData, loading: leagueLoading } = useApi(`/api/league-stats/${id}?limit=100`);
  const { data: leaguesData } = useApi('/api/leagues');

  const players = leagueData?.stats || [];
  const league = leaguesData?.leagues?.find((l) => String(l.id) === String(id));
  const leagueName = league?.name || `League #${id}`;

  const totalGoals = players.reduce((sum, p) => sum + (p.total_goals || 0), 0);
  const totalPlayers = players.length;
  const avgGPG = totalPlayers > 0
    ? (players.reduce((sum, p) => sum + (p.goals_per_game || 0), 0) / totalPlayers).toFixed(2)
    : '0.00';

  const insight = generateLeagueInsight(players, leagueName);

  if (leagueLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div>
          <div className="h-4 w-24 rounded bg-surface-container-high mb-4" />
          <div className="h-9 w-72 max-w-full rounded-lg bg-surface-container-high mb-3" />
          <div className="flex gap-2">
            <div className="h-5 w-24 rounded-full bg-surface-container-high" />
            <div className="h-5 w-16 rounded-full bg-surface-container-high" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-surface-container border border-outline-variant/30 rounded-xl p-5 h-[92px]" />
          ))}
        </div>
        <div className="bg-surface-container border border-outline-variant/30 rounded-xl overflow-hidden">
          <div className="px-4 sm:px-6 py-5 border-b border-outline-variant/30">
            <div className="h-3 w-28 rounded bg-surface-container-high mb-2" />
            <div className="h-5 w-32 rounded bg-surface-container-high" />
          </div>
          <div className="p-4 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-12 rounded-lg bg-surface-container-high/60" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <button
          onClick={() => navigate('/leagues')}
          className="inline-flex min-h-[40px] items-center gap-1 mb-2 font-label text-xs font-semibold uppercase tracking-[0.08em] text-primary hover:gap-2 transition-all duration-150"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          All Leagues
        </button>
        <p className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant mb-2">
          League
        </p>
        <h2 className="font-expanded text-3xl sm:text-4xl font-headline font-extrabold tracking-tight text-on-surface mb-3">
          {leagueName}
        </h2>
        {league && (
          <div className="flex flex-wrap gap-2">
            <span className="px-2 py-1 bg-primary/10 text-primary border border-primary/20 text-[10px] font-label font-semibold rounded-full uppercase tracking-[0.12em]">
              {league.region_name}
            </span>
            <span className="px-2 py-1 bg-secondary/10 text-secondary border border-secondary/20 text-[10px] font-label font-semibold rounded-full uppercase tracking-[0.12em]">
              Step {league.step_number}
            </span>
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total Goals" value={totalGoals} accentColor="text-secondary" />
        <StatCard label="Players" value={totalPlayers} />
        <StatCard label="Avg GPG" value={avgGPG} accentColor="text-primary" />
      </div>

      {insight && <InsightCard body={insight.text} />}

      <section className="bg-surface-container rounded-xl overflow-hidden border border-outline-variant/30">
        <div className="px-4 sm:px-6 py-5 border-b border-outline-variant/30">
          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant mb-1">
            Golden Boot Race
          </p>
          <h3 className="font-headline font-bold text-lg text-on-surface">Top Scorers</h3>
        </div>
        <PlayerTable players={players} />
      </section>
    </div>
  );
}
